import { useEffect, useMemo, useRef, useState } from "react";

interface CheckboxDropdownOption {
  value: string;
  label: string;
  /** Small grey line rendered under the label. Useful for context like
   *  track + car names for an event option. */
  subtitle?: string;
}

interface CheckboxDropdownProps {
  label: string;
  options: CheckboxDropdownOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  /** Render a search input at the top of the open panel. Filters by label
   *  and subtitle (case-insensitive). Off by default — only worth enabling
   *  for long lists. */
  searchable?: boolean;
  searchPlaceholder?: string;
}

export function CheckboxDropdown({
  label,
  options,
  selected,
  onChange,
  searchable = false,
  searchPlaceholder = "Search…",
}: CheckboxDropdownProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [open]);

  // Reset search when the panel closes; auto-focus when it opens.
  useEffect(() => {
    if (!open) {
      setSearch("");
    } else if (searchable) {
      // Delay one frame so the input is mounted before we focus.
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [open, searchable]);

  function toggle(value: string) {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  }

  const filteredOptions = useMemo(() => {
    if (!searchable) return options;
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) =>
      opt.label.toLowerCase().includes(q) ||
      (opt.subtitle ?? "").toLowerCase().includes(q),
    );
  }, [options, search, searchable]);

  const count = selected.length;
  const buttonLabel = count === 0 ? label : `${label} (${count})`;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary ${
          count > 0 ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-gray-300 bg-white text-gray-700"
        }`}
      >
        {buttonLabel}
        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-20 mt-1 min-w-64 max-w-sm rounded-lg border border-gray-200 bg-white shadow-lg max-h-72 overflow-hidden flex flex-col">
          {searchable && (
            <div className="sticky top-0 bg-white border-b border-gray-100 p-2 flex-shrink-0">
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className="block w-full rounded border border-gray-200 px-2 py-1 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>
          )}
          <div className="overflow-y-auto py-1">
            {options.length === 0 ? (
              <p className="px-3 py-2 text-sm text-gray-400">No options.</p>
            ) : filteredOptions.length === 0 ? (
              <p className="px-3 py-2 text-sm text-gray-400">No matching options.</p>
            ) : filteredOptions.map((opt) => (
              <label
                key={opt.value}
                className="flex items-start gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(opt.value)}
                  onChange={() => toggle(opt.value)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 flex-shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate">{opt.label}</div>
                  {opt.subtitle && (
                    <div className="text-xs text-gray-500 truncate">{opt.subtitle}</div>
                  )}
                </div>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
